import * as angular from 'angular';
import { find } from 'lodash';
import { first } from 'rxjs/operators';

import { Resource } from '@mode/shared/util-halogen';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { ToastConfig, ToastService } from '@mode/shared/util-toast';
import { OrganizationResource } from '@mode/legacy/data-access-model-js';

import { SamlIdentityResource } from '@mode/legacy/data-access-model-js';
import { AppStore } from '~/types/main.types';
import { getAppName } from '@mode/shared/util-js';

export interface SamlFormItem {
  samlIdentity: SamlIdentityResource;
  defaultCollapsed: boolean;
  defaultIsEditing: boolean;
}

export const IdentitySaml: angular.IComponentOptions = {
  controller: class {
    //
    // Properties
    public organization: OrganizationResource;
    public samlIdentityCollection: Resource;
    public samlFormList: SamlFormItem[];
    readonly appName = getAppName();
    workspaceTitle = 'Workspace';

    static $inject = ['IdentitySamlService', 'ToastService', '$scope', 'RxStoreService'];

    constructor(
      private IdentitySamlService,
      private alertService: ToastService,
      private $scope: angular.IScope,
      private rxStoreService: AppStore
    ) {}

    $onInit() {
      this.IdentitySamlService.getSamlForms(this.samlIdentityCollection)
        .then((samlForms) => {
          this.samlFormList = samlForms;
        })
        .catch((err) => {
          BugsnagHelpers.notifySafely(err);
          console.error(err);
        });
    }

    handleAddNewProviderClick() {
      this.IdentitySamlService.createNewSamlForm(this.organization)
        .then((newSamlForm) => {
          const newSamlList = [newSamlForm].concat(this.samlFormList);
          this.samlFormList = newSamlList;
        })
        .catch((err) => {
          BugsnagHelpers.notifySafely(err);
          console.error(err);
        });
    }

    findTargetSaml(providerToken: string): SamlFormItem {
      return find(this.samlFormList, (provider) => provider.samlIdentity.token === providerToken);
    }

    updateSamlFormListWithProvider(providerToken: string, updatedSaml: SamlFormItem) {
      this.samlFormList = this.samlFormList.map((provider) => {
        if (provider.samlIdentity.token === providerToken) {
          return updatedSaml;
        }
        return provider;
      });
    }

    updateProviderSuccess(providerToken: string, updatedSaml: SamlFormItem) {
      this.updateSamlFormListWithProvider(providerToken, updatedSaml);
      this.alertService.success('Saml provider successfully updated.');
    }

    updateProviderFailure(err) {
      BugsnagHelpers.notifySafely(err);
      console.error(err);
      // require the user to dismiss the error by clicking on the toast notification
      const toastConfig: ToastConfig<unknown, unknown> = { disableTimeout: true };
      this.alertService.error(err.message, toastConfig);
    }

    destroyProviderSuccess(providerToken) {
      this.$scope.$applyAsync(() => {
        this.samlFormList = this.samlFormList.filter((provider) => provider.samlIdentity.token !== providerToken);
      });
      this.alertService.success('Saml provider successfully deleted.');
    }

    handleDestroyProvider(providerToken) {
      const targetSamlItem = this.findTargetSaml(providerToken);
      this.IdentitySamlService.destroySamlProvider(targetSamlItem)
        .then(() => {
          this.destroyProviderSuccess(providerToken);
        })
        .catch((err) => {
          BugsnagHelpers.notifySafely(err);
          console.error(err);
        });
    }

    handleUpdateProvider(providerToken, editableDisplayName, editableProviderSsoUrl, editableCertificate) {
      const targetSamlItem = this.findTargetSaml(providerToken);
      const newFields = {
        'saml_identity_provider.name': editableDisplayName,
        'saml_identity_provider.target_url': editableProviderSsoUrl,
        'saml_identity_provider.certificate': editableCertificate,
      };
      this.IdentitySamlService.updateSamlProvider(targetSamlItem, newFields)
        .then((updatedSaml) => {
          this.updateProviderSuccess(providerToken, updatedSaml);
        })
        .catch((err) => {
          this.updateProviderFailure(err);
        });
    }

    handleToggleEnabledProvider(providerToken: string) {
      const targetSamlItem = this.findTargetSaml(providerToken);
      this.IdentitySamlService.toggleEnabledForSamlProvider(targetSamlItem)
        .then((updatedSaml) => {
          this.updateSamlFormListWithProvider(providerToken, updatedSaml);
        })
        .catch((err) => {
          this.updateProviderFailure(err);
        });
    }
  },
  controllerAs: 'ctrl',
  bindings: {
    samlIdentityCollection: '<',
    organization: '<',
  },
  template: `
    <div class="identity-saml-container">
      <div class="section-header">
        <div class="section-header-description">
          <h4>
            SAML
          </h4>
          <p>
            Custom providers will automatically create user accounts in {{ ctrl.appName }} for emails that match your {{ ctrl.workspaceTitle }}’s
            claimed domain. Setup is required for each provider.
          </p>
        </div>

        <div class="section-header-aside">
          <div class="btn btn-lg btn-optional-action" ng-click="ctrl.handleAddNewProviderClick()">
            Add new provider
          </div>
        </div>
      </div>
    </div>

    <identity-saml-form
      ng-repeat="samlProvider in ctrl.samlFormList"
      display-name="samlProvider.samlIdentity.name"
      provider-sso-url="samlProvider.samlIdentity.provider_sso_url"
      certificate="samlProvider.samlIdentity.certificate"
      assertion-url="samlProvider.samlIdentity.assertion_url"
      entity-id="samlProvider.samlIdentity.entity_id"
      provider-token="samlProvider.samlIdentity.token"
      default-collapsed="samlProvider.defaultCollapsed"
      on-destroy-provider="ctrl.handleDestroyProvider($providerToken)"
      on-update-provider="ctrl.handleUpdateProvider($providerToken, $editableDisplayName, $editableProviderSsoUrl, $editableCertificate)"
      provider-is-default="samlProvider.samlIdentity.default"
      enabled="samlProvider.samlIdentity.enabled"
      on-toggle-enabled="ctrl.handleToggleEnabledProvider($providerToken)"
      default-is-editing="samlProvider.defaultIsEditing"
    />
  </div>
  `,
};
